import React, { Component } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { AvatarPicker, FormGrid, OptionAutocomplete, OptionSelect } from '../../../../ui';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import noavatar from '../../../../public/images/noavatar.png';
import actions from '../../../../redux/actions';
import { Gender } from '../../../../types/Gender';
import { OperationStatus } from '../../../../types/OperationStatus';
import countryCodes from '../../../../util/options/countryCodes';
import genderOptions from '../../../../util/options/genders';
import languages from '../../../../util/options/languages';
import userSourceOptions from '../../../../util/options/userSources';
import PasswordChangeModal from '../../../PasswordChangeModal';

const mapStateToProps = (state) => {
  return {
    userSaveStatus: state.userSaveStatus,
    userUploadAvatarStatus: state.userUploadAvatarStatus,
    passwordChangeStatus: state.passwordChangeStatus,
    user: state.user,
    devices: state.devices,
    isMobile: state.resolution.device === 'mobile' || state.resolution.device === 'mobile-tablet',
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(actions.updateUser(data)),
    saveUser: (data: any, notify?: boolean) => dispatch(actions.saveUser(data, notify)),
    uploadAvatar: (avatarFile) => dispatch(actions.uploadAvatar(avatarFile)),
  };
};

const genderOptionsNoAny = genderOptions.filter((el) => el.key !== Gender.Any);

type PersonalState = {
  newAvatar: any;
  newAvatarError: string;
  errors: Set<string>;
};

class Personal extends Component<any> {
  public state: PersonalState;

  constructor(props) {
    super(props);

    this.state = {
      newAvatar: {},
      newAvatarError: '',
      errors: new Set(),
    };
  }

  resetErrorState = (event) => {
    const name = event.target.name;
    const errors = this.state.errors;

    if (errors.has(name)) {
      errors.delete(name);
      this.setState({ errors });
    }
  };

  onChange = (event, data) => {
    const user = {};

    switch (data.name) {
      case 'sendInvitations': {
        user[data.name] = data.checked;
        break;
      }

      case 'age': {
        data.value = data.value.replace(/^[0]+/g, '');
        user[data.name] = data.value;
        break;
      }

      default: {
        user[data.name] = data.value;
        break;
      }
    }

    return this.props.updateUser(user);
  };

  validateInput = (event, data) => {
    const errors = this.state.errors;
    const name = data && data.name ? data.name : event.target.name;
    const value = data && data.value ? data.value : event.target.value;

    if (name === 'age') {
      if (!value.length || parseInt(value) < 0 || isNaN(Number(value)) || parseInt(value) > 120) {
        errors.add(name);
      } else {
        errors.delete(name);
      }

      this.setState({ errors });
      return;
    }

    if (name === 'minHourlyRate') {
      if (!value.length || parseInt(value) < 0 || isNaN(Number(value))) {
        errors.add(name);
      } else {
        errors.delete(name);
      }

      this.setState({ errors });
      return;
    }

    if (name === 'source') {
      if (!value) {
        errors.add(name);
      } else {
        errors.delete(name);
      }

      this.setState({ errors });
      return;
    }

    if (!value || !value.length) {
      errors.add(name);
    } else {
      errors.delete(name);
    }

    this.setState({ errors });
  };

  onAvatarUpload = ([acceptedImageFile]) => {
    const newAvatar = Object.assign(acceptedImageFile, {
      preview: URL.createObjectURL(acceptedImageFile),
    });

    this.setState({
      newAvatar,
      newAvatarError: '',
    });
  };

  onAvatarRejected = () => {
    this.setState({ newAvatarError: 'Avatar size can be no more than 1 Mb' });
  };

  onSave = async () => {
    const userData: Record<string, any> = Object.keys(this.props.user).reduce((result, el) => {
      if (
        this.props.user[el] !== null &&
        el !== 'cycles' // cycles are managed separately
      ) {
        result[el] = this.props.user[el];
      }

      return result;
    }, {});

    delete userData.avatarUrl;

    if (this.state.newAvatar && Object.keys(this.state.newAvatar).length) {
      await this.props.uploadAvatar(this.state.newAvatar);
    }

    this.props.saveUser(userData, true);
  };

  render() {
    const userSaveProcessing = this.props.userSaveStatus.status === OperationStatus.processing || this.props.userUploadAvatarStatus.status === OperationStatus.processing;
    const user = this.props.user;
    const isMobile = this.props.isMobile;

    let avatarUrl;

    if (this.state.newAvatar.preview) {
      avatarUrl = this.state.newAvatar.preview;
    } else if (user.avatarUrl) {
      avatarUrl = user.avatarUrl;
    } else {
      avatarUrl = noavatar;
    }

    const errors = this.state.errors;

    // Adapters to the (event, { name, value }) handlers above
    const change = (name: string, value: any) => this.onChange(null, { name, value });
    const focus = (name: string) => () => this.resetErrorState({ target: { name } });
    const blur = (name: string) => (event?: any) => {
      const value = event?.target?.value ?? user[name] ?? '';
      this.validateInput({ target: { name, value } }, { name, value });
    };
    const text = (name: string, label: string, props: any = {}) => (
      <TextField
        name={name}
        label={label}
        value={user[name] ?? ''}
        error={errors.has(name)}
        onChange={(event) => change(name, event.target.value)}
        onFocus={focus(name)}
        onBlur={blur(name)}
        fullWidth
        {...props}
      />
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <AvatarPicker
          src={avatarUrl}
          error={this.state.newAvatarError}
          hint="A good profile image makes a client feel more secure"
          onPick={(file) => this.onAvatarUpload([file])}
          onReject={this.onAvatarRejected}
        />

        <FormGrid isMobile={isMobile}>
          {text('firstName', 'First name')}
          {text('lastName', 'Last name')}
          <OptionSelect
            name="gender"
            label="Gender"
            options={genderOptionsNoAny}
            value={user.gender}
            error={errors.has('gender')}
            onValueChange={(value) => change('gender', value)}
          />
          {text('age', 'Age', { type: 'number', inputProps: { min: 16 }, value: user.age || '' })}
          <OptionAutocomplete label="Country" options={countryCodes} value={user.country} onValueChange={(value) => change('country', value)} disabled />
          <OptionAutocomplete
            label="Native language"
            options={languages}
            value={user.nativeLanguage}
            error={errors.has('nativeLanguage')}
            onValueChange={(value) => change('nativeLanguage', value)}
            onBlur={blur('nativeLanguage')}
          />
          <OptionAutocomplete label="Other language" options={languages} value={user.otherLanguage} onValueChange={(value) => change('otherLanguage', value)} />
          <OptionSelect
            name="source"
            label="How did you reach us?"
            options={userSourceOptions}
            value={user.source}
            error={errors.has('source')}
            onValueChange={(value) => change('source', value)}
          />
        </FormGrid>

        {text('about', 'About yourself', { multiline: true, minRows: 3 })}

        <FormGrid isMobile={isMobile}>
          {text('minHourlyRate', 'Minimum hourly rate', {
            type: 'number',
            InputProps: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
            helperText: "You won't get invitations to cycles paying less than this. 0 means \"offer me anything\".",
            FormHelperTextProps: { sx: { color: errors.has('minHourlyRate') ? 'error.main' : 'text.secondary' } },
          })}
          <FormControlLabel
            control={<Switch checked={!!user.sendInvitations} onChange={(event) => this.onChange(null, { name: 'sendInvitations', checked: event.target.checked })} />}
            label="Send me testing invitations"
            sx={{ alignSelf: 'flex-start', mt: 1 }}
          />
        </FormGrid>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <PasswordChangeModal />
          <Button variant="contained" disabled={userSaveProcessing || errors.size > 0} onClick={this.onSave}>
            {userSaveProcessing ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Personal));
